import React, { useEffect, useState } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import {
  Alert, Box, Button, CircularProgress,
  Snackbar, ThemeProvider, createTheme, CssBaseline,
} from '@mui/material';
import Layout from './components/Layout';
import AmbientBackground from './components/AmbientBackground';
import Dashboard from './components/Dashboard';
import TrucksTable from './components/TrucksTable';
import TrucksCategoryPage from './components/TrucksCategoryPage';
import PartsTable from './components/PartsTable';
import InventoryManagement from './components/InventoryManagement';
import TruckDetail from './components/TruckDetail';
import PartMovements from './components/PartMovements';
import Drivers from './components/Drivers';
import DriverCheckIn from './components/DriverCheckIn';
import AllServiceLogs from './components/AllServiceLogs';
import MaintenanceReminders from './components/MaintenanceReminders';
import LiveGps from './components/LiveGps';
import TruckScheduling from './components/TruckScheduling';
import MechanicDefectChecklist from './components/MechanicDefectChecklist';
import ResolvedDefectsTable from './components/ResolvedDefectsTable';
import OdometerConfirmHost from './components/OdometerConfirmHost';
import AuthPage from './components/AuthPage';
import ResetPasswordPage from './components/ResetPasswordPage';
import UserManagement from './components/UserManagement';
import ProfilePage from './components/ProfilePage';
import PrivacyPolicy from './components/PrivacyPolicy';
import { AuthProvider, useAuth } from './auth/AuthContext';
import {
  PROTECTED_DELETE_SUCCEEDED_EVENT,
} from './api/api';

function DeleteSuccessPopup() {
  const [message, setMessage] = useState('');

  useEffect(() => {
    const handleSuccess = (event) => setMessage(event.detail?.message || 'Deleted successfully.');
    window.addEventListener(PROTECTED_DELETE_SUCCEEDED_EVENT, handleSuccess);
    return () => window.removeEventListener(PROTECTED_DELETE_SUCCEEDED_EVENT, handleSuccess);
  }, []);

  return (
    <Snackbar
      open={Boolean(message)}
      autoHideDuration={5000}
      onClose={() => setMessage('')}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert severity="success" variant="filled" onClose={() => setMessage('')} sx={{ width: '100%' }}>
        {message}
      </Alert>
    </Snackbar>
  );
}

function AccessDenied() {
  const { defaultPath } = useAuth();
  return (
    <Box sx={{ p: 3 }}>
      <Alert
        severity="warning"
        action={(
          <Button color="inherit" size="small" onClick={() => { window.location.hash = defaultPath; }}>
            Go Back
          </Button>
        )}
      >
        You do not have access to this section.
      </Alert>
    </Box>
  );
}

function ProtectedRoute({ path, children }) {
  const { canAccessPath } = useAuth();
  return canAccessPath(path) ? children : <AccessDenied />;
}

function RoleDefaultRedirect() {
  const { defaultPath } = useAuth();
  return <Navigate to={defaultPath} replace />;
}

const theme = createTheme({
  typography: {
    fontSize: 13,
    h1: { fontSize: '2.9rem' },
    h2: { fontSize: '2.45rem' },
    h3: { fontSize: '2.05rem' },
    h4: { fontSize: '1.7rem' },
    h5: { fontSize: '1.35rem' },
    h6: { fontSize: '1.1rem' },
    body1: { fontSize: '0.94rem' },
    body2: { fontSize: '0.82rem' },
    button: { fontSize: '0.82rem' },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          fontSize: '90%',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          minHeight: 34,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: '0.9rem',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '0.82rem',
          paddingTop: 10,
          paddingBottom: 10,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        label: {
          fontSize: '0.78rem',
        },
      },
    },
  },
});

function AuthenticatedApp() {
  const { loading, isAuthenticated, defaultPath } = useAuth();
  const [hash, setHash] = useState(() => window.location.hash || '');

  useEffect(() => {
    const updateHash = () => setHash(window.location.hash || '');
    window.addEventListener('hashchange', updateHash);
    return () => window.removeEventListener('hashchange', updateHash);
  }, []);

  if (hash === '#/privacy') {
    return <PrivacyPolicy />;
  }

  if (hash.startsWith('#/reset-password')) {
    return <ResetPasswordPage />;
  }

  if (loading) {
    return (
      <Box sx={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) return <AuthPage />;

  return (
    <>
      <OdometerConfirmHost />
      <DeleteSuccessPopup />
      <Router>
        <Layout>
          <Routes>
            <Route path="/" element={<Navigate to={defaultPath} replace />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/dashboard" element={<ProtectedRoute path="/dashboard"><Dashboard /></ProtectedRoute>} />
            <Route path="/trucks" element={<ProtectedRoute path="/trucks"><TrucksTable /></ProtectedRoute>} />
            <Route path="/trucks/:categorySlug" element={<ProtectedRoute path="/trucks"><TrucksCategoryPage /></ProtectedRoute>} />
            <Route path="/parts" element={<ProtectedRoute path="/parts"><PartsTable /></ProtectedRoute>} />
            <Route path="/inventory" element={<ProtectedRoute path="/inventory"><InventoryManagement /></ProtectedRoute>} />
            <Route path="/movements" element={<ProtectedRoute path="/movements"><PartMovements /></ProtectedRoute>} />
            <Route path="/live-gps" element={<ProtectedRoute path="/live-gps"><LiveGps /></ProtectedRoute>} />
            <Route path="/truck-scheduling" element={<ProtectedRoute path="/truck-scheduling"><TruckScheduling /></ProtectedRoute>} />
            <Route path="/mechanic-defects" element={<ProtectedRoute path="/mechanic-defects"><MechanicDefectChecklist /></ProtectedRoute>} />
            <Route path="/resolved-defects" element={<ProtectedRoute path="/resolved-defects"><ResolvedDefectsTable /></ProtectedRoute>} />
            <Route path="/truck/:id" element={<ProtectedRoute path="/truck/:id"><TruckDetail /></ProtectedRoute>} />
            <Route path="/drivers" element={<ProtectedRoute path="/drivers"><Drivers /></ProtectedRoute>} />
            <Route path="/drivers/check-in" element={<ProtectedRoute path="/drivers/check-in"><Navigate to="/drivers/check-in/pre_trip" replace /></ProtectedRoute>} />
            <Route path="/drivers/check-in/:type" element={<ProtectedRoute path="/drivers/check-in/:type"><DriverCheckIn /></ProtectedRoute>} />
            <Route path="/service-logs" element={<ProtectedRoute path="/service-logs"><AllServiceLogs /></ProtectedRoute>} />
            <Route path="/maintenance-reminders" element={<ProtectedRoute path="/maintenance-reminders"><MaintenanceReminders /></ProtectedRoute>} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/users" element={<ProtectedRoute path="/users"><UserManagement /></ProtectedRoute>} />
            <Route path="*" element={<RoleDefaultRedirect />} />
          </Routes>
        </Layout>
      </Router>
    </>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ position: 'relative', isolation: 'isolate', minHeight: '100dvh', width: '100%' }}>
        <AmbientBackground />
        <AuthProvider>
          <AuthenticatedApp />
        </AuthProvider>
      </Box>
    </ThemeProvider>
  );
}

export default App;
